import axios from "axios";
import { API_URL } from "configs";

export const PAF_ENGINE_PATHS = Object.freeze({
  dashboard: "appraisals/paf-settings/dashboard",
  simulate: "appraisals/paf-settings/simulate",
  generate: "appraisals/paf-settings/generate",
  runs: "appraisals/paf-settings/runs",
  logs: "appraisals/paf-settings/logs",
  globalSettings: "appraisals/paf-global-settings",
  testingGenerateCurrent: "appraisals/paf-settings/testing/generate-current",
  testingClearPreview: "appraisals/paf-settings/testing/clear-preview",
  testingClear: "appraisals/paf-settings/testing/clear",
  policies: "appraisals/paf-generation-policies",
  policyById: (id) => `appraisals/paf-generation-policies/${id}`,
  appraisalTypeOptions: "appraisals/appraisal-types/options",
});

export const STRATEGY_OPTIONS = [
  { value: "company_schedule", label: "Company Schedule" },
  { value: "hire_date", label: "Hire Date" },
];

export const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export const MINUTES_PER_DAY = 1440;

/** Total minutes accepted by Global Settings. Labels are what the admin sees. */
export const DEADLINE_OFFSET_OPTIONS = [
  { value: 2, label: "2 Minutes" },
  { value: 5, label: "5 Minutes" },
  { value: 10, label: "10 Minutes" },
  { value: 15, label: "15 Minutes" },
  { value: 30, label: "30 Minutes" },
  { value: 60, label: "1 Hour" },
  { value: 120, label: "2 Hours" },
  { value: 1440, label: "1 Day" },
  { value: 2880, label: "2 Days" },
  { value: 7200, label: "5 Days" },
];

const SUPPORTED_OFFSET_MINUTES = new Set(DEADLINE_OFFSET_OPTIONS.map((option) => option.value));

const OFFSET_FIELD_LABELS = {
  scoring_offset_days: "scoring/action deadline",
  approval_offset_days: "approval deadline",
};

export const emptyDeadlineOffsets = () => ({
  offset_unit: "minutes",
  monthly: { scoring_offset_days: "", approval_offset_days: "" },
  quarterly: { scoring_offset_days: "", approval_offset_days: "" },
  yearly: { scoring_offset_days: "", approval_offset_days: "" },
});

/**
 * Normalize API/UI deadline_offsets into total minutes (or empty strings).
 * Payloads without offset_unit=minutes are legacy whole days and are scaled by 1440.
 * Invalid values become empty strings so the settings page can still render.
 */
export const normalizeDeadlineOffsets = (raw) => {
  const base = emptyDeadlineOffsets();
  if (!raw || typeof raw !== "object") {
    return base;
  }

  const legacyDays = raw.offset_unit !== "minutes";

  FREQUENCY_OPTIONS.forEach(({ value: frequency }) => {
    const bucket = raw[frequency];
    if (!bucket || typeof bucket !== "object") {
      return;
    }
    base[frequency] = {
      scoring_offset_days: scaleOffsetToMinutes(bucket.scoring_offset_days, legacyDays),
      approval_offset_days: scaleOffsetToMinutes(bucket.approval_offset_days, legacyDays),
      scoring_offset_label:
        typeof bucket.scoring_offset_label === "string" ? bucket.scoring_offset_label : "",
      approval_offset_label:
        typeof bucket.approval_offset_label === "string" ? bucket.approval_offset_label : "",
    };
  });

  return base;
};

const toOffsetInputValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return "";
};

const scaleOffsetToMinutes = (value, legacyDays) => {
  const parsed = toOffsetInputValue(value);
  if (parsed === "") {
    return "";
  }
  return legacyDays ? parsed * MINUTES_PER_DAY : parsed;
};

export const formatOffsetMinutes = (minutes) => {
  const known = DEADLINE_OFFSET_OPTIONS.find((option) => option.value === minutes);
  if (known) {
    return known.label;
  }
  if (typeof minutes === "number" && minutes > 0 && minutes % MINUTES_PER_DAY === 0) {
    const days = minutes / MINUTES_PER_DAY;
    return `${days} ${days === 1 ? "Day" : "Days"}`;
  }
  if (typeof minutes === "number" && minutes > 0 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
  }
  return `${minutes} Minutes`;
};

export const deadlineOffsetSelectOptions = (currentValue, currentLabel = "") => {
  if (typeof currentValue !== "number" || SUPPORTED_OFFSET_MINUTES.has(currentValue)) {
    return DEADLINE_OFFSET_OPTIONS;
  }

  const label = currentLabel || `${formatOffsetMinutes(currentValue)} (existing setting)`;

  return [{ value: currentValue, label }, ...DEADLINE_OFFSET_OPTIONS];
};

export const isSupportedOffsetMinutes = (value) =>
  typeof value === "number" && SUPPORTED_OFFSET_MINUTES.has(value);

/**
 * Client-side validation matching UpdatePafGlobalSettingRequest.
 * Values are total minutes and must be one of the supported durations.
 * @returns {Record<string, string>} field path → message
 */
export const validateDeadlineOffsetsClient = (offsets) => {
  const errors = {};
  FREQUENCY_OPTIONS.forEach(({ value: frequency, label }) => {
    const bucket = offsets?.[frequency];
    if (!bucket || typeof bucket !== "object") {
      errors[`deadline_offsets.${frequency}`] = `${label} deadline offsets are required.`;
      return;
    }
    ["scoring_offset_days", "approval_offset_days"].forEach((key) => {
      const value = bucket[key];
      const path = `deadline_offsets.${frequency}.${key}`;
      const fieldLabel = OFFSET_FIELD_LABELS[key] || "deadline offset";
      if (value === null || value === undefined || value === "") {
        errors[path] = `${label} ${fieldLabel} is required.`;
        return;
      }
      const numeric = typeof value === "string" && /^\d+$/.test(value.trim())
        ? Number(value.trim())
        : value;
      if (typeof numeric !== "number" || !Number.isInteger(numeric) || numeric < 0) {
        errors[path] = `${label} ${fieldLabel} must be one of the supported durations.`;
        return;
      }
      if (!SUPPORTED_OFFSET_MINUTES.has(numeric)) {
        errors[path] = `${label} ${fieldLabel} must be one of the supported durations (2 minutes through 5 days).`;
      }
    });
  });
  return errors;
};

/** Generation testing intervals. Separate from deadline offsets. */
export const TESTING_INTERVAL_OPTIONS = [
  { value: 2, label: "2 Minutes" },
  { value: 5, label: "5 Minutes" },
  { value: 10, label: "10 Minutes" },
  { value: 15, label: "15 Minutes" },
  { value: 30, label: "30 Minutes" },
  { value: 60, label: "1 Hour" },
  { value: 120, label: "2 Hours" },
  { value: 1440, label: "1 Day" },
  { value: 2880, label: "2 Days" },
  { value: 7200, label: "5 Days" },
];

export const TESTING_ACTION_OPTIONS = [
  { value: "email_reminders", label: "Email Reminders" },
  { value: "auto_approve", label: "Auto-approve" },
  { value: "auto_employee_sign", label: "Auto Employee Sign" },
];

export const emptyTestingIntervals = () => ({
  monthly: "",
  quarterly: "",
  yearly: "",
  email_reminders: "",
  auto_approve: "",
  auto_employee_sign: "",
});

export const testingIntervalsFromApi = (schedulerTesting) => {
  const base = emptyTestingIntervals();
  [...FREQUENCY_OPTIONS, ...TESTING_ACTION_OPTIONS].forEach(({ value }) => {
    const minutes = schedulerTesting?.intervals?.[value]?.interval_minutes;
    if (typeof minutes === "number" && TESTING_INTERVAL_OPTIONS.some((option) => option.value === minutes)) {
      base[value] = minutes;
    }
  });
  return base;
};

export const generateCurrentTestPafs = async (objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.testingGenerateCurrent}`,
    withObject(objectViewedId),
    { headers: authHeaders() }
  );
  return data;
};

export const previewClearTestPafs = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.testingClearPreview}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const clearTestPafs = async (objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.testingClear}`,
    withObject(objectViewedId, { confirm: true }),
    { headers: authHeaders() }
  );
  return data;
};

export const ELIGIBILITY_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "probationary", label: "Probationary" },
  { value: "both", label: "Both" },
];

export const WORKFLOW_OPTIONS = [
  { value: "one_level", label: "One Level" },
  { value: "two_level", label: "Two Level" },
  { value: "three_level", label: "Three Level" },
  { value: "four_level", label: "Four Level" },
];

export const PROBATION_UNIT_OPTIONS = [
  { value: "day", label: "Days" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
];

export const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((m) => ({ value: m, label: m }));

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const withObject = (objectViewedId, payload = {}) =>
  objectViewedId ? { ...payload, object_viewed_id: objectViewedId } : payload;

export const fetchEngineDashboard = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.dashboard}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const fetchGlobalSettings = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.globalSettings}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const updateGlobalSettings = async (payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${PAF_ENGINE_PATHS.globalSettings}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const fetchPolicies = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.policies}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  const list = Array.isArray(data) ? data : data?.data || [];
  return list;
};

export const createPolicy = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.policies}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const updatePolicy = async (id, payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const deletePolicy = async (id, objectViewedId) => {
  await axios.delete(`${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`, {
    headers: authHeaders(),
    data: withObject(objectViewedId),
  });
};

export const fetchAppraisalTypeOptions = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.appraisalTypeOptions}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  const list = Array.isArray(data) ? data : data?.data || [];
  return list.map((row) => ({
    value: row.id || row.value,
    label: row.name || row.label,
    code: row.code,
  }));
};

export const simulateGeneration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.simulate}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const runGeneration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.generate}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const fetchGenerationLogs = async (objectViewedId, filters = {}) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.logs}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId, filters),
  });
  return data?.data || [];
};

export const fetchGenerationRuns = async (objectViewedId, filters = {}) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.runs}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId, filters),
  });
  return data?.data || [];
};

export const exportEngineConfiguration = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}appraisals/paf-settings/export`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const importEngineConfiguration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}appraisals/paf-settings/import`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const fetchPolicyById = async (id, objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const getErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status === 422) {
    const errors = response.data?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey ? errors[firstKey]?.[0] : null;
      if (firstMessage) return String(firstMessage);
    }
    return response.data?.message || "Validation failed.";
  }
  return (
    response?.data?.message ||
    response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};
